import type { ViewName, ViewSizeMm, PagePlacement } from "../types";
import { TOP_BOX, BOTTOM_BOX } from "../constant";
import { estimateViewSizeInPixels, maxScaleForArea } from "./scale";
import { packZoneWithSkyline } from "../packing/skyline";

export const tryPackPagesWithEstimatedSizes = (views: ViewName[], sizesMm: ViewSizeMm, scale: number) => {
    for (const viewName of views) {
        const maxTop = maxScaleForArea(sizesMm[viewName].w, sizesMm[viewName].h, TOP_BOX.width, TOP_BOX.height);
        const maxBottom = maxScaleForArea(
            sizesMm[viewName].w,
            sizesMm[viewName].h,
            BOTTOM_BOX.width,
            BOTTOM_BOX.height
        );
        if (scale > Math.max(maxTop, maxBottom) + 1e-6) return { ok: false as const, pages: [] as PagePlacement[] };
    }

    const estimatedPixels: Record<ViewName, { w: number; h: number }> = Object.fromEntries(
        views.map((viewName) => {
            const { wPx, hPx } = estimateViewSizeInPixels(viewName, sizesMm, scale);
            return [viewName, { w: wPx, h: hPx }];
        })
    ) as Record<ViewName, { w: number; h: number }>;

    const pages: PagePlacement[] = [];
    let remainingViews = [...views];

    while (remainingViews.length) {
        const rects = remainingViews.map((viewName) => ({ name: viewName, ...estimatedPixels[viewName] }));
        const topPack = packZoneWithSkyline(TOP_BOX.width, TOP_BOX.height, rects);
        const leftoverAfterTop = rects.filter((rect) => !topPack.placed.some((p) => p.name === rect.name));
        const bottomPack = packZoneWithSkyline(BOTTOM_BOX.width, BOTTOM_BOX.height, leftoverAfterTop);
        const unplacedNames = leftoverAfterTop
            .filter((rect) => !bottomPack.placed.some((p) => p.name === rect.name))
            .map((r) => r.name);

        if (topPack.placed.length === 0 && bottomPack.placed.length === 0) {
            return { ok: false as const, pages: [] as PagePlacement[] };
        }

        pages.push({ top: topPack.placed as any, bottom: bottomPack.placed as any });

        remainingViews = remainingViews.filter(
            (viewName) => ![...topPack.placed, ...bottomPack.placed].some((p: any) => p.name === viewName)
        );
        if (unplacedNames.length && remainingViews.length === 0) remainingViews = unplacedNames as any;
    }

    return { ok: true as const, pages };
};

export const chooseScaleAndPages = (views: ViewName[], sizesMm: ViewSizeMm, minScale: number) => {
    let upperBound = Infinity;
    for (const viewName of views) {
        const scaleTop = maxScaleForArea(sizesMm[viewName].w, sizesMm[viewName].h, TOP_BOX.width, TOP_BOX.height);
        const scaleBottom = maxScaleForArea(
            sizesMm[viewName].w,
            sizesMm[viewName].h,
            BOTTOM_BOX.width,
            BOTTOM_BOX.height
        );
        upperBound = Math.min(upperBound, Math.max(scaleTop, scaleBottom));
    }
    if (!Number.isFinite(upperBound)) upperBound = 1;

    let lowerBound = Math.max(0.0001, minScale);
    if (lowerBound > upperBound) lowerBound = upperBound;

    let bestScale = lowerBound;
    let bestPages: PagePlacement[] = [];
    let bestScore = Number.POSITIVE_INFINITY;

    for (let i = 0; i < 25; i++) {
        const candidateScale = (lowerBound + upperBound) / 2;
        const packed = tryPackPagesWithEstimatedSizes(views, sizesMm, candidateScale);
        if (packed.ok) {
            const score = packed.pages.length * 1e9 - candidateScale * 1e6;
            if (score < bestScore) {
                bestScore = score;
                bestScale = candidateScale;
                bestPages = packed.pages;
            }
            lowerBound = candidateScale;
        } else {
            upperBound = candidateScale;
        }
    }

    return { scale: bestScale, pages: bestPages };
};

export const packPagesWithExactSizes = (views: ViewName[], sizesPx: Record<ViewName, { w: number; h: number }>) => {
    const pages: PagePlacement[] = [];
    let remainingViews = [...views];

    while (remainingViews.length) {
        const rects = remainingViews.map((viewName) => ({
            name: viewName,
            w: sizesPx[viewName].w,
            h: sizesPx[viewName].h,
        }));
        const topPack = packZoneWithSkyline(TOP_BOX.width, TOP_BOX.height, rects);
        const notInTop = rects.filter((rect) => !topPack.placed.some((p) => p.name === rect.name));
        const bottomPack = packZoneWithSkyline(BOTTOM_BOX.width, BOTTOM_BOX.height, notInTop);

        pages.push({ top: topPack.placed as any, bottom: bottomPack.placed as any });

        const placedNames = new Set<string>([
            ...topPack.placed.map((p: any) => p.name),
            ...bottomPack.placed.map((p: any) => p.name),
        ]);

        remainingViews = remainingViews.filter((viewName) => !placedNames.has(viewName));
        // @ts-ignore
        remainingViews = [...new Set(remainingViews)];

        if (topPack.placed.length === 0 && bottomPack.placed.length === 0) {
            const first = remainingViews[0];
            pages.push({
                top: [
                    {
                        name: first,
                        x: 0,
                        y: 0,
                        w: Math.min(sizesPx[first].w, TOP_BOX.width),
                        h: Math.min(sizesPx[first].h, TOP_BOX.height),
                    } as any,
                ],
                bottom: [],
            });
            remainingViews = remainingViews.slice(1);
            continue;
        }
    }
    return pages;
};
